import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Upload } from 'lucide-react';
import { Button } from '@shared/ui';
import type { AssetBalanceDto, AutoWithdrawalDto } from '@shared/api/contracts';
import {
  getAutoWithdrawalSettingsStatus,
  normalizeAutoWithdrawalDto,
} from '@entities/withdrawal';
import { AutoWithdrawalModal, useWithdrawalsLocked, WithdrawalModal } from '@features/withdrawal';

interface WithdrawalActionsProps {
  /** Spendable amount. Null means it is unknown and must not be replaced with the booked total. */
  availableBalance: number | null;
  totalBalance: number | null;
  assetBalances: AssetBalanceDto[];
  autoWithdraw: AutoWithdrawalDto | null;
  isBalanceLoading?: boolean;
  balanceError?: unknown;
  onRetryBalance?: () => void;
  onUpdate?: () => void;
}

export function WithdrawalActions({
  availableBalance,
  totalBalance,
  assetBalances,
  autoWithdraw,
  isBalanceLoading = false,
  balanceError,
  onRetryBalance,
  onUpdate,
}: WithdrawalActionsProps) {
  const { t } = useTranslation('cabinet');
  const [isWithdrawalOpen, setIsWithdrawalOpen] = useState(false);
  const [isAutoWithdrawalOpen, setIsAutoWithdrawalOpen] = useState(false);

  const normalizedSettings = useMemo(() => normalizeAutoWithdrawalDto(autoWithdraw), [autoWithdraw]);
  const settingsStatus = getAutoWithdrawalSettingsStatus(normalizedSettings);
  const isAutoActive = settingsStatus === 'active';
  const withdrawalsLocked = useWithdrawalsLocked();

  const statusLabels = {
    none: 'withdrawal.auto.status.notConfigured',
    pending: 'withdrawal.auto.status.pending',
    partial: 'withdrawal.auto.status.partial',
    active: 'withdrawal.auto.status.active',
  } as const;

  return (
    <>
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:gap-3">
        <Button
          variant="default"
          className="h-10 w-full rounded-xl px-4 text-sm font-semibold sm:h-11 sm:w-auto sm:px-6"
          onClick={() => setIsWithdrawalOpen(true)}
          disabled={withdrawalsLocked}
        >
          <Upload className="mr-2 h-4 w-4" />
          {t('withdrawal.withdraw')}
        </Button>
        <Button
          className={`h-10 w-full rounded-xl px-4 text-sm font-semibold sm:h-11 sm:w-auto sm:px-6 ${
            isAutoActive
              ? 'bg-success text-success-foreground hover:bg-success/90'
              : 'bg-secondary text-foreground hover:bg-secondary/80'
          }`}
          onClick={() => setIsAutoWithdrawalOpen(true)}
        >
          <Settings className="mr-2 h-4 w-4" />
          {t('withdrawal.auto.button')}: {t(statusLabels[settingsStatus])}
        </Button>
      </div>

      <WithdrawalModal
        open={isWithdrawalOpen}
        onOpenChange={setIsWithdrawalOpen}
        type="merchants"
        assetBalances={assetBalances}
        walletBalance={availableBalance ?? undefined}
        isBalanceLoading={isBalanceLoading}
        balanceError={balanceError}
        onRetryBalance={onRetryBalance}
      />

      <AutoWithdrawalModal
        open={isAutoWithdrawalOpen}
        onOpenChange={setIsAutoWithdrawalOpen}
        autoWithdraw={normalizedSettings}
        assetBalances={assetBalances}
        totalBalance={totalBalance ?? undefined}
        availableBalance={availableBalance ?? undefined}
        onSaved={onUpdate}
      />
    </>
  );
}
